import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { hasVaultKey } from "@/hooks/useKeyVault";
import { AlertTriangle, CheckCircle2, Lock, LogOut, RefreshCw, XCircle } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";

// Optional fields throughout: public payload omits paths' raw `path`, `ttma.config`,
// `ttma.reason`, and `diagnostics`. Operator (auth-gated) payload includes all fields.
interface WritablePath {
  path?: string;
  exists: boolean;
  writable: boolean;
  error?: string | null;
}

interface DiagnosticEntry {
  id: number;
  timestamp: string;
  level: "info" | "warn" | "error";
  source: string;
  message: string;
}

interface HealthPayload {
  status: string;
  timestamp: string;
  deployment?: {
    commit?: string;
  };
  services?: {
    web: string;
    ttma: string;
    storage: string;
  };
  ttma: {
    available: boolean;
    reason?: string;
    config?: {
      groq_key?: boolean;
      groq_models?: string[];
      alpha_vantage_key?: boolean;
      polygon_key?: boolean;
      finnhub_key?: boolean;
      nyt_key?: boolean;
      cmc_key?: boolean;
    };
  };
  paths: Record<string, WritablePath>;
  diagnostics?: DiagnosticEntry[];
}

const OPERATOR_AUTH_KEY = "tsm_operator_auth";

const StatusIcon: React.FC<{ ok: boolean }> = ({ ok }) =>
  ok ? <CheckCircle2 size={16} aria-hidden="true" /> : <XCircle size={16} aria-hidden="true" />;

const StatusPage: React.FC = () => {
  const [health, setHealth] = useState<HealthPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [operatorAuth, setOperatorAuth] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(OPERATOR_AUTH_KEY);
  });

  const isOperator = Boolean(operatorAuth);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = operatorAuth ? "/api/health/detailed" : "/api/health";
      const headers: Record<string, string> = {};
      if (operatorAuth) headers.Authorization = `Basic ${operatorAuth}`;
      const res = await fetch(endpoint, { headers });
      const data = await res.json();
      if (res.status === 401 && operatorAuth) {
        // Stale or invalid creds — drop to public mode.
        window.sessionStorage.removeItem(OPERATOR_AUTH_KEY);
        setOperatorAuth(null);
        setError("Operator session expired. Showing public status only.");
        return;
      }
      if (!res.ok) throw new Error(data?.error ?? data?.message ?? `Server returned ${res.status}`);
      setHealth(data as HealthPayload);
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOperatorLogin = () => {
    const user = window.prompt("Operator username");
    if (!user) return;
    const pass = window.prompt("Operator password");
    if (!pass) return;
    const encoded = window.btoa(`${user}:${pass}`);
    window.sessionStorage.setItem(OPERATOR_AUTH_KEY, encoded);
    setOperatorAuth(encoded);
  };

  const handleOperatorLogout = () => {
    window.sessionStorage.removeItem(OPERATOR_AUTH_KEY);
    setOperatorAuth(null);
  };

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 15000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorAuth]);

  const pathRows = useMemo(() => Object.entries(health?.paths ?? {}), [health]);
  const hasErrorLogs = health?.diagnostics?.some((entry) => entry.level === "error") ?? false;

  return (
    <>
      <Helmet>
        <title>System Status & Diagnostics | Tylee Smart Markets</title>
        <meta name="description" content="Live runtime status for Tylee Smart Markets: web shell, research engine, report archive storage, deploy marker, BYOK key checks, and recent diagnostics." />
        <link rel="canonical" href="https://tyl.ee/status" />
        <meta property="og:title" content="System Status — Tylee Smart Markets" />
        <meta property="og:description" content="Runtime status, deploy marker, storage checks, and recent diagnostics for TSM research tools." />
        <meta property="og:url" content="https://tyl.ee/status" />
        <meta name="twitter:title" content="System Status — Tylee Smart Markets" />
        <meta name="twitter:description" content="Runtime status, deploy marker, storage checks, and recent diagnostics for TSM research tools." />
      </Helmet>
      <div className="tsm-home-shell theme-amber">
        <AppHeader brandText="System Status" />
        <main className="status-main">
        <section className="status-hero" aria-labelledby="status-title">
          <div>
            <p className="home-kicker">OPERATIONS · HEALTH · DIAGNOSTICS</p>
            <h1 id="status-title">Runtime status and recent diagnostics</h1>
            <p>
              The checks below show whether the web shell can reach TTMA, whether report storage is
              writable, and which deploy commit is currently serving the research tools.
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button className="analytics-action-btn" type="button" onClick={load} disabled={loading}>
              <RefreshCw size={15} aria-hidden="true" />
              <span>{loading ? "Refreshing" : "Refresh"}</span>
            </button>
            {isOperator ? (
              <button className="analytics-action-btn" type="button" onClick={handleOperatorLogout}>
                <LogOut size={15} aria-hidden="true" />
                <span>Operator sign out</span>
              </button>
            ) : (
              <button className="analytics-action-btn" type="button" onClick={handleOperatorLogin}>
                <Lock size={15} aria-hidden="true" />
                <span>Operator login</span>
              </button>
            )}
          </div>
        </section>

        {isOperator && (
          <section className="status-alert" style={{ background: "rgba(74, 158, 255, 0.1)", border: "1px solid #4a9eff", borderRadius: "6px", padding: "12px 16px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px" }}>
            <Lock size={15} aria-hidden="true" />
            <span>Operator mode active — viewing detailed diagnostics</span>
          </section>
        )}

        {error && (
          <section className="status-alert status-alert--error">
            <AlertTriangle size={17} aria-hidden="true" />
            <span>{error}</span>
          </section>
        )}

        <section className="status-grid" aria-label="Service checks">
          <div className={`status-tile ${health?.status === "ok" ? "status-ok" : "status-bad"}`}>
            <StatusIcon ok={health?.status === "ok"} />
            <span>Web shell</span>
            <strong>{health?.status ?? "checking"}</strong>
          </div>
          <div className={`status-tile ${health?.ttma?.available ? "status-ok" : "status-bad"}`}>
            <StatusIcon ok={health?.ttma?.available ?? false} />
            <span>Research engine</span>
            <strong>{health?.ttma?.available ? "available" : health?.ttma?.reason ?? "checking"}</strong>
          </div>
          <div className={`status-tile ${hasErrorLogs ? "status-warn" : "status-ok"}`}>
            {hasErrorLogs ? <AlertTriangle size={16} aria-hidden="true" /> : <CheckCircle2 size={16} aria-hidden="true" />}
            <span>Diagnostics</span>
            <strong>{hasErrorLogs ? "errors present" : "no recent errors"}</strong>
          </div>
          <div className={`status-tile ${health?.deployment?.commit ? "status-ok" : "status-warn"}`}>
            <StatusIcon ok={Boolean(health?.deployment?.commit)} />
            <span>Deploy marker</span>
            <strong>{health?.deployment?.commit ? health.deployment.commit.slice(0, 7) : "not reported"}</strong>
          </div>
          <div className={`status-tile ${health?.paths?.ttmaReports?.writable ? "status-ok" : "status-bad"}`}>
            <StatusIcon ok={health?.paths?.ttmaReports?.writable ?? false} />
            <span>Research reports</span>
            <strong>{health?.paths?.ttmaReports?.writable ? "writable" : "checking"}</strong>
          </div>
        </section>

        <section className="status-panel" aria-labelledby="write-paths-title">
          <h2 id="write-paths-title">Storage Writability</h2>
          <div className="status-path-list">
            {pathRows.map(([name, item]) => (
              <div key={name} className={`status-path-row ${item.writable ? "status-ok" : "status-bad"}`}>
                <StatusIcon ok={item.writable} />
                <span>{name}</span>
                {item.path && <code>{item.path}</code>}
                {item.error && <small>{item.error}</small>}
              </div>
            ))}
          </div>
        </section>

        {isOperator && health?.ttma?.available && (
          <section className="status-panel" aria-labelledby="config-title">
            <h2 id="config-title">TTMA Configuration</h2>
            <p className="status-empty">Browser-vault keys unlock Digital Asset Trader, TradFi Research, Digital Assets Research, email delivery, and market-data enrichment.</p>
            <div className="status-config-grid">
              {([
                ["Groq key", hasVaultKey("groq")],
                ["Gemini key", hasVaultKey("gemini")],
                ["OpenRouter key", hasVaultKey("openrouter")],
                ["Alpha Vantage", hasVaultKey("alphaVantage")],
                ["Polygon", hasVaultKey("polygon")],
                ["Finnhub", hasVaultKey("finnhub")],
                ["NYT", hasVaultKey("nyt")],
                ["NewsAPI", hasVaultKey("newsapi")],
                ["CoinMarketCap", hasVaultKey("cmc")],
                ["FMP", hasVaultKey("fmp")],
                ["FRED", hasVaultKey("fred")],
                ["EIA", hasVaultKey("eia")],
              ] as [string, boolean][]).map(([label, present]) => (
                <div key={label} className={`status-config-pill ${present ? "status-ok" : "status-warn"}`}>
                  <StatusIcon ok={present} />
                  <span>{label}</span>
                  <strong>{present ? "configured" : "not set"}</strong>
                </div>
              ))}
              {health.ttma.config?.groq_models && (
                <div className="status-config-pill status-ok">
                  <CheckCircle2 size={16} aria-hidden="true" />
                  <span>Groq models</span>
                  <strong>{health.ttma.config.groq_models.join(", ") || "defaults"}</strong>
                </div>
              )}
            </div>
          </section>
        )}

        {isOperator && health?.diagnostics && (
          <section className="status-panel" aria-labelledby="diag-title">
            <h2 id="diag-title">Recent Diagnostics</h2>
            <div className="status-log-list">
              {health.diagnostics.map((entry) => (
                <article key={entry.id} className={`status-log-row status-log-row--${entry.level}`}>
                  <time dateTime={entry.timestamp}>{new Date(entry.timestamp).toLocaleTimeString()}</time>
                  <span>{entry.level.toUpperCase()}</span>
                  <strong>{entry.source}</strong>
                  <p>{entry.message}</p>
                </article>
              ))}
              {health.diagnostics.length === 0 && (
                <p className="status-empty">No diagnostics recorded since the current server process started.</p>
              )}
            </div>
          </section>
        )}

        {!isOperator && (
          <section className="status-panel">
            <h2>Operator-only sections</h2>
            <p>Detailed diagnostics, TTMA configuration, and recent log entries are operator-only. Sign in above to view full diagnostic data.</p>
          </section>
        )}
      </main>
      <Footer />
      </div>
    </>
  );
};

export default StatusPage;
